import { toIsoSeconds } from '@/admin/audit/auditFilters';

/**
 * Filtres de l'ecran « Conflits » : modele, lecture de l'URL et requete serveur.
 *
 * Meme principe que le journal d'audit : les filtres vivent **dans l'URL**, ce
 * qui les conserve a l'ouverture du detail (`useDetailRoute` garde la chaine de
 * requete), apres un rechargement et dans un lien partage — c'est aussi ce qui
 * permet a la fiche d'un compte d'y renvoyer (`?userId=`). Module **pur**.
 *
 * Chaque filtre est execute par le serveur et son `total` le reflete : rien
 * n'est filtre ici. La periode filtre la **liste** sur la date de detection ;
 * les compteurs, eux, appliquent chacun leur propre date (`ConflictCounters`).
 */

export type ConflictPeriod = 'all' | '24h' | '7d' | '30d';

export interface ConflictFilters {
  readonly status: string;
  readonly classification: string;
  readonly entityType: string;
  readonly userId: string;
  readonly period: ConflictPeriod;
}

/**
 * Toute la periode par defaut, contrairement au journal d'audit : un conflit
 * ouvert depuis trois semaines est justement ce qu'on vient chercher, et une
 * fenetre de sept jours le ferait disparaitre. Le registre est une table de
 * quelques lignes, pas un journal qui grossit a chaque requete.
 */
export const DEFAULT_CONFLICT_FILTERS: ConflictFilters = {
  status: '',
  classification: '',
  entityType: '',
  userId: '',
  period: 'all',
};

export const CONFLICT_PERIOD_OPTIONS: readonly { readonly value: ConflictPeriod; readonly label: string }[] = [
  { value: 'all', label: 'Toute la période' },
  { value: '24h', label: '24 dernières heures' },
  { value: '7d', label: '7 derniers jours' },
  { value: '30d', label: '30 derniers jours' },
];

/** La periode, en toutes lettres, pour dire sous chaque compteur sur quoi il porte. */
export const periodPhrase = (period: ConflictPeriod): string =>
  ({
    all: 'depuis la mise en service du registre',
    '24h': 'sur les 24 dernières heures',
    '7d': 'sur les 7 derniers jours',
    '30d': 'sur les 30 derniers jours',
  })[period];

const PERIOD_MILLISECONDS: Readonly<Partial<Record<ConflictPeriod, number>>> = {
  '24h': 24 * 3_600_000,
  '7d': 7 * 24 * 3_600_000,
  '30d': 30 * 24 * 3_600_000,
};

const isPeriod = (value: string | null): value is ConflictPeriod =>
  value === 'all' || value === '24h' || value === '7d' || value === '30d';

/** Page demandee : un entier superieur ou egal a 1, sinon 1. */
export const readConflictPage = (params: URLSearchParams): number => {
  const parsed = Number.parseInt(params.get('page') ?? '', 10);

  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1;
};

export const readConflictFilters = (params: URLSearchParams): ConflictFilters => {
  const period = params.get('period');

  return {
    status: params.get('status') ?? '',
    classification: params.get('classification') ?? '',
    entityType: params.get('entityType') ?? '',
    userId: params.get('userId') ?? '',
    period: isPeriod(period) ? period : DEFAULT_CONFLICT_FILTERS.period,
  };
};

/** Chaine de requete de l'URL : une valeur par defaut n'y figure pas. */
export const writeConflictFilters = (filters: ConflictFilters, page = 1): URLSearchParams => {
  const params = new URLSearchParams();

  for (const key of ['status', 'classification', 'entityType', 'userId'] as const) {
    if (filters[key] !== '') {
      params.set(key, filters[key]);
    }
  }

  if (filters.period !== DEFAULT_CONFLICT_FILTERS.period) {
    params.set('period', filters.period);
  }

  if (page > 1) {
    params.set('page', String(page));
  }

  return params;
};

/**
 * Parametres de requete de `GET /admin/synchronization-conflicts`. La periode
 * relative est ancree sur `now`, **lu a chaque chargement** : « Actualiser »
 * deplace donc la fenetre, ce qu'une borne figee dans l'URL ne ferait pas.
 */
export const toConflictQuery = (filters: ConflictFilters, now: Date): Record<string, string> => {
  const query: Record<string, string> = {};
  const span = PERIOD_MILLISECONDS[filters.period];

  for (const key of ['status', 'classification', 'entityType', 'userId'] as const) {
    if (filters[key] !== '') {
      query[key] = filters[key];
    }
  }

  if (span !== undefined) {
    query.from = toIsoSeconds(new Date(now.getTime() - span));
  }

  return query;
};

/** Un filtre differe-t-il de l'ouverture ? Pilote « Réinitialiser les filtres ». */
export const hasActiveConflictFilter = (filters: ConflictFilters): boolean => writeConflictFilters(filters).size > 0;
